/**
 * Visibilidad y facultades de la pantalla de Caja bajo cierre ciego.
 *
 * `organizations.blind_cash_close` hace que el cajero declare el arqueo real sin
 * ver la referencia teorica. El flag solo tapaba KPIs y columnas del modal, asi
 * que el mismo cajero abria el "Historial interno" (o /sistema/caja/detalle/:id)
 * y leia teorico y diferencia. Eso se cerro con el permiso
 * `caja-cierre-completo`.
 *
 * Quedaban igual tres agujeros que vaciaban el control:
 *  - la pestaña "Turno" lista TODAS las ventas y movimientos con importe: el
 *    cajero sumaba y reconstruia el teorico antes de declarar;
 *  - podia imprimir el comprobante de su propio cierre;
 *  - podia anular movimientos de caja (con rollback en movimientos_financieros
 *    y cuenta corriente de proveedores) sin ningun permiso aparte.
 *
 * Ahora cada una de esas facultades es un permiso por modulo, configurable por
 * rol en Configuracion -> Roles y por usuario en Usuarios y Roles, con la misma
 * maquinaria de `role_templates` + `role_permissions` que el resto de los
 * accesos. No hace falta columna nueva por permiso.
 *
 * REGLA DE ALCANCE (deliberada): los permisos SOLO restringen cuando la
 * organizacion tiene el cierre ciego activo. Sin el flag todo sigue como antes,
 * asi que ninguna organizacion existente pierde funciones al actualizar.
 */

export const FULL_CASH_CLOSE_MODULE_ID = 'caja-cierre-completo';
export const CASH_MOVEMENTS_MODULE_ID = 'caja-ver-movimientos';
export const CASH_RECENT_MOVEMENTS_MODULE_ID = 'caja-ver-ultimos-movimientos';
export const CASH_PRINT_MODULE_ID = 'caja-imprimir-cierre';
export const CASH_VOID_MODULE_ID = 'caja-anular-movimiento';

/** Cuantos movimientos ve quien solo tiene `caja-ver-ultimos-movimientos`. */
export const DEFAULT_RECENT_MOVEMENTS_LIMIT = 5;
export const MAX_RECENT_MOVEMENTS_LIMIT = 50;

const PRIVILEGED_ROLES = new Set(['admin', 'administrador']);

// `caja-cierre-completo` es el permiso de "ve el arqueo entero": quien lo tiene
// ya lee teorico y diferencia, asi que negarle la lista de movimientos o la
// impresion no protegeria nada. La anulacion NO se implica: es una accion
// destructiva sobre plata, no una vista.
const IMPLIED_BY_FULL_CLOSE = new Set([
  CASH_MOVEMENTS_MODULE_ID,
  CASH_RECENT_MOVEMENTS_MODULE_ID,
  CASH_PRINT_MODULE_ID,
]);

// Ver la lista completa implica el subconjunto de los ultimos X.
const IMPLIED_BY_FULL_MOVEMENTS = new Set([CASH_RECENT_MOVEMENTS_MODULE_ID]);

const toModuleSet = (effectiveModules) => {
  if (!effectiveModules) return new Set();
  if (effectiveModules instanceof Set) return effectiveModules;
  if (Array.isArray(effectiveModules)) return new Set(effectiveModules);
  return new Set();
};

/**
 * Resuelve un permiso fino de caja.
 *
 * @param {object} [params]
 * @param {string} [params.moduleId] permiso a evaluar (uno de los *_MODULE_ID)
 * @param {boolean} [params.blindCashCloseEnabled] organizations.blind_cash_close
 * @param {object} [params.profile] perfil del usuario logueado
 * @param {boolean} [params.isSuperAdmin] soporte de la plataforma
 * @param {Set<string>|string[]} [params.effectiveModules] permisos efectivos
 * @returns {boolean}
 */
export function hasCashModulePermission({
  moduleId,
  blindCashCloseEnabled = false,
  profile = null,
  isSuperAdmin = false,
  effectiveModules = null,
} = {}) {
  // Sin cierre ciego no hay nada que ocultar: se conserva el comportamiento
  // historico para no romper a las organizaciones que hoy no usan el flag.
  if (!blindCashCloseEnabled) return true;

  if (isSuperAdmin) return true;

  const role = String(profile?.role || '').trim().toLowerCase();
  // El dueno siempre ve. Ademas de ser quien configura el flag, hay orgs sin
  // `role_templates` sembradas donde el admin no tiene ningun modulo efectivo
  // (mismo fallback que ya aplica Sistema.jsx); sin esta excepcion se quedaria
  // sin acceso a su propia caja.
  if (PRIVILEGED_ROLES.has(role)) return true;

  if (!profile) return false;

  const modules = toModuleSet(effectiveModules);
  if (modules.has('all')) return true;
  if (modules.has(moduleId)) return true;

  if (IMPLIED_BY_FULL_CLOSE.has(moduleId) && modules.has(FULL_CASH_CLOSE_MODULE_ID)) return true;
  if (IMPLIED_BY_FULL_MOVEMENTS.has(moduleId) && modules.has(CASH_MOVEMENTS_MODULE_ID)) return true;

  return false;
}

/**
 * @returns {boolean} true si puede ver historial, detalle del arqueo y
 *                    teorico/diferencia impresos.
 */
export function canViewFullCashClose(params = {}) {
  return hasCashModulePermission({ ...params, moduleId: FULL_CASH_CLOSE_MODULE_ID });
}

/** Lista completa de ventas y movimientos del turno. */
export function canViewCashMovements(params = {}) {
  return hasCashModulePermission({ ...params, moduleId: CASH_MOVEMENTS_MODULE_ID });
}

/** Solo los ultimos N movimientos del turno (control de lo recien cargado). */
export function canViewRecentCashMovements(params = {}) {
  return hasCashModulePermission({ ...params, moduleId: CASH_RECENT_MOVEMENTS_MODULE_ID });
}

/** Imprimir el comprobante del arqueo / cierre. */
export function canPrintCashClose(params = {}) {
  return hasCashModulePermission({ ...params, moduleId: CASH_PRINT_MODULE_ID });
}

/** Anular movimientos de caja (con rollback financiero y de proveedores). */
export function canVoidCashMovements(params = {}) {
  return hasCashModulePermission({ ...params, moduleId: CASH_VOID_MODULE_ID });
}

/**
 * Normaliza `organizations.blind_cash_recent_movements`. Un valor invalido,
 * ausente o fuera de rango cae al default en lugar de mostrar todo o nada.
 */
export function resolveRecentMovementsLimit(rawValue) {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return DEFAULT_RECENT_MOVEMENTS_LIMIT;
  const truncated = Math.trunc(parsed);
  if (truncated <= 0) return DEFAULT_RECENT_MOVEMENTS_LIMIT;
  return Math.min(truncated, MAX_RECENT_MOVEMENTS_LIMIT);
}
